// ══════════════════════════════════════════════════════════
// RADIOS SERVICE — Supabase Direct
//
// Operaciones CRUD de radios usando Supabase + RLS.
// La asignación automática de frecuencia usa la función
// PostgreSQL assign_next_available_frequency().
//
// NO usa backend PHP para CRUD normal.
// NO conecta CentovaCast.
// ══════════════════════════════════════════════════════════

import { getSupabaseClient } from '@/services/supabase/client';
import type {
  Radio,
  RadioInsert,
  RadioUpdate,
  RadioMembership,
  RadioSettings,
  RadioSettingsUpdate,
  FrequencyAssignment,
  FrequencyRequest,
  FrequencyRequestInsert,
} from '@/types/radios';

// ── HELPERS ──

function supabase() {
  return getSupabaseClient();
}

// ═══════════════ RADIOS ═══════════════

/**
 * Listar radios permitidas por RLS.
 * Un usuario autenticado ve sus propias radios (vía membership/org)
 * + las radios públicas activas. Anónimo solo ve públicas activas.
 */
export async function listRadios(): Promise<Radio[]> {
  const { data, error } = await supabase()
    .from('radios')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Error al listar radios: ${error.message}`);
  return (data as Radio[]) || [];
}

/**
 * Obtener una radio por ID.
 * RLS garantiza que solo se devuelva si el usuario tiene acceso.
 */
export async function getRadio(id: string): Promise<Radio | null> {
  const { data, error } = await supabase()
    .from('radios')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw new Error(`Error al obtener radio: ${error.message}`);
  return (data as Radio) || null;
}

/**
 * Obtener una radio por slug.
 */
export async function getRadioBySlug(slug: string): Promise<Radio | null> {
  const { data, error } = await supabase()
    .from('radios')
    .select('*')
    .eq('slug', slug)
    .maybeSingle();

  if (error) throw new Error(`Error al obtener radio por slug: ${error.message}`);
  return (data as Radio) || null;
}

/**
 * Crear una radio nueva + asignar frecuencia automática.
 *
 * Flujo:
 * 1. Inserta la radio (RLS: belongs_to_organization).
 * 2. Llama a assign_next_available_frequency(radio_id)
 *    que busca desde 87.00, avanza 0.01, sin tope en 200.00.
 * 3. Retorna la radio creada con su frecuencia asignada.
 */
export async function createRadio(
  input: RadioInsert,
): Promise<{ radio: Radio; frequency: number }> {
  // 1. Insertar la radio
  const { data: radio, error: insertError } = await supabase()
    .from('radios')
    .insert(input)
    .select('*')
    .single();

  if (insertError) {
    throw new Error(`Error al crear radio: ${insertError.message}`);
  }

  // 2. Asignar frecuencia automática mediante la función segura
  const { data: frequency, error: freqError } = await supabase()
    .rpc('assign_next_available_frequency', {
      p_radio_id: radio.id,
    });

  if (freqError) {
    // Si falla la frecuencia, la radio ya está creada pero sin frecuencia.
    // El usuario puede reintentar o pedir soporte.
    throw new Error(
      `Radio creada pero falló la asignación de frecuencia: ${freqError.message}`,
    );
  }

  return { radio: radio as Radio, frequency: frequency as number };
}

/**
 * Actualizar datos editoriales de una radio.
 * RLS + trigger de Bloque 8 bloquean cambio de organization_id.
 */
export async function updateRadio(
  id: string,
  input: RadioUpdate,
): Promise<Radio> {
  const { data, error } = await supabase()
    .from('radios')
    .update(input)
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw new Error(`Error al actualizar radio: ${error.message}`);
  return data as Radio;
}

// ═══════════════ RADIO SETTINGS ═══════════════

/**
 * Obtener configuración de una radio.
 */
export async function getRadioSettings(
  radioId: string,
): Promise<RadioSettings | null> {
  const { data, error } = await supabase()
    .from('radio_settings')
    .select('*')
    .eq('radio_id', radioId)
    .maybeSingle();

  if (error) throw new Error(`Error al obtener configuración: ${error.message}`);
  return (data as RadioSettings) || null;
}

/**
 * Actualizar o crear configuración de una radio.
 */
export async function upsertRadioSettings(
  radioId: string,
  input: RadioSettingsUpdate,
): Promise<RadioSettings> {
  const existing = await getRadioSettings(radioId);

  if (existing) {
    const { data, error } = await supabase()
      .from('radio_settings')
      .update(input)
      .eq('radio_id', radioId)
      .select('*')
      .single();

    if (error) throw new Error(`Error al actualizar configuración: ${error.message}`);
    return data as RadioSettings;
  }

  const { data, error } = await supabase()
    .from('radio_settings')
    .insert({ radio_id: radioId, settings: input.settings })
    .select('*')
    .single();

  if (error) throw new Error(`Error al crear configuración: ${error.message}`);
  return data as RadioSettings;
}

// ═══════════════ FREQUENCY ═══════════════

/**
 * Obtener la frecuencia activa de una radio.
 */
export async function getActiveFrequency(
  radioId: string,
): Promise<FrequencyAssignment | null> {
  const { data, error } = await supabase()
    .from('frequency_assignments')
    .select('*')
    .eq('radio_id', radioId)
    .eq('is_active', true)
    .maybeSingle();

  if (error) throw new Error(`Error al obtener frecuencia activa: ${error.message}`);
  return (data as FrequencyAssignment) || null;
}

/**
 * Obtener todas las asignaciones de frecuencia de una radio.
 */
export async function getFrequencyHistory(
  radioId: string,
): Promise<FrequencyAssignment[]> {
  const { data, error } = await supabase()
    .from('frequency_assignments')
    .select('*')
    .eq('radio_id', radioId)
    .order('assigned_at', { ascending: false });

  if (error) throw new Error(`Error al obtener historial de frecuencias: ${error.message}`);
  return (data as FrequencyAssignment[]) || [];
}

// ═══════════════ FREQUENCY REQUESTS ═══════════════

/**
 * Solicitar una frecuencia especial.
 *
 * Reglas (reforzadas por triggers del Bloque 8):
 * - La frecuencia debe estar disponible.
 * - El estado inicial es 'pending'.
 * - El frontend NO puede marcar como pagada ni aprobada.
 * - La frecuencia automática actual permanece activa hasta
 *   que la solicitud sea pagada y aprobada por administración.
 *
 * NO conecta pagos todavía.
 */
export async function requestSpecialFrequency(
  input: FrequencyRequestInsert,
): Promise<FrequencyRequest> {
  const { data, error } = await supabase()
    .from('frequency_requests')
    .insert({
      radio_id: input.radio_id,
      organization_id: input.organization_id,
      requested_frequency: input.requested_frequency,
      reason: input.reason || null,
      status: 'pending',
    })
    .select('*')
    .single();

  if (error) {
    // El trigger trg_check_frequency_request_availability lanza
    // EXCEPTION si la frecuencia ya está ocupada.
    throw new Error(`Error al solicitar frecuencia especial: ${error.message}`);
  }

  return data as FrequencyRequest;
}

/**
 * Obtener solicitudes de frecuencia de una radio.
 */
export async function getFrequencyRequests(
  radioId: string,
): Promise<FrequencyRequest[]> {
  const { data, error } = await supabase()
    .from('frequency_requests')
    .select('*')
    .eq('radio_id', radioId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Error al obtener solicitudes de frecuencia: ${error.message}`);
  return (data as FrequencyRequest[]) || [];
}

/**
 * Cancelar una solicitud de frecuencia especial (solo si está pending).
 */
export async function cancelFrequencyRequest(
  requestId: string,
): Promise<FrequencyRequest> {
  const { data, error } = await supabase()
    .from('frequency_requests')
    .update({ status: 'cancelled' })
    .eq('id', requestId)
    .eq('status', 'pending')
    .select('*')
    .single();

  if (error) throw new Error(`Error al cancelar solicitud: ${error.message}`);
  return data as FrequencyRequest;
}

// ═══════════════ MEMBERSHIPS ═══════════════

/**
 * Obtener membresías de una radio.
 */
export async function getRadioMemberships(
  radioId: string,
): Promise<RadioMembership[]> {
  const { data, error } = await supabase()
    .from('radio_memberships')
    .select('*')
    .eq('radio_id', radioId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Error al obtener membresías: ${error.message}`);
  return (data as RadioMembership[]) || [];
}